import type { Observable } from 'rxjs';
import { zip, of } from 'rxjs';
import { mergeMap, map, catchError } from 'rxjs/operators';

import { request } from 'app/utils/request';
import {
  getPandapiUrl,
  getPandapiHeaders,
  getApiUrl,
  getTangerineHeaders,
  getHashiUrl,
  getHashiHeaders,
  getControleBackendUrl
} from 'app/utils/api';
import { removeItemWhen, uniques } from 'app/utils/array';
import { hasAccessToFeature } from 'app/utils/permissions';
import type { MatchBlueprint } from 'app/pages/BlueprintsPage/types';
import type { CompetitorOddsWithStatus } from 'app/pages/EventsPage/types';

import type { MatchData, HashiData } from './types';
import { getUpdatesForBetbuilder } from './utils';

interface WsMarketBlockInsert {
  type: 'market';
  action: 'inserted';
  model: Market;
}

interface WsMarketBlockUpdate {
  type: 'market';
  action: 'updated';
  model: Market;
}

interface WsMarketBlockDelete {
  type: 'market';
  action: 'deleted';
  model_id: string;
}

interface WsMarketBlockRefresh {
  type: 'refresh_markets';
  action: 'update';
  model_id: number; // match id
}

interface WsMatchUpdate {
  type: 'match';
  action: string;
  model_id: number;
}

interface WsBettingDataUpdate {
  type: 'betting_metadata';
  action: 'updated';
  model: {
    event_type: 'Match';
    event_id: number;
    betting_metadata: PandapiBettingData;
  };
}

interface WsIncidentUpdate {
  type: 'incident';
  action: 'updated';
  model: MatchIncident;
}

interface WsIncidentDelete {
  type: 'incident';
  action: 'deleted';
  model: MatchIncident;
}

interface WsTraderInputsUpdate {
  type: 'trader_inputs';
  action: 'updated';
  model: TraderInputs;
}

interface WsBlueprintCreated {
  type: 'match_blueprint';
  action: 'created';
  model: MatchBlueprint;
}

interface WsBlueprintUpdate {
  type: 'match_blueprint';
  action: 'updated';
  model: MatchBlueprint;
}

interface HttpUpdateMarket {
  type: 'market';
  action: 'http_update';
  model: Market[];
}

interface ClearMarkets {
  type: 'clear_markets';
  action: 'unbook';
}

interface WsPandascoreMarketBlockInsert {
  type: 'pandascore_market';
  action: 'inserted';
  model: Market;
}

interface WsPandascoreMarketBlockUpdate {
  type: 'pandascore_market';
  action: 'updated';
  model: Market;
}

interface WsPandascoreMarketBlockDelete {
  type: 'pandascore_market';
  action: 'deleted';
  model_id: string;
}

interface HttpUpdatePandascoreMarket {
  type: 'pandascore_market';
  action: 'http_update';
  model: Market[];
}

interface WsSettlement {
  type: 'settlements';
  action: 'updated';
  model: GameSettlement[];
}

interface WsRealtimeBetlog {
  type: 'betlog_market_metrics';
  action: 'updated';
  model_id: number;
  model: RealtimeBetlogMarket[];
}

interface WsSetTradedWithGrid {
  type: 'set_traded_with_grid';
}

interface WsUnsetTradedWithGrid {
  type: 'unset_traded_with_grid';
}

interface WsSetTradedWithScraper {
  type: 'set_traded_with_scraper';
}

interface WsUnsetTradedWithScraper {
  type: 'unset_traded_with_scraper';
}

type MatchMessage =
  | WsMarketBlockInsert
  | WsMarketBlockUpdate
  | WsMarketBlockDelete
  | WsMarketBlockRefresh
  | WsMatchUpdate
  | WsBettingDataUpdate
  | WsIncidentUpdate
  | WsIncidentDelete
  | WsTraderInputsUpdate
  | WsBlueprintCreated
  | WsBlueprintUpdate
  | HttpUpdateMarket
  | ClearMarkets
  | WsPandascoreMarketBlockInsert
  | WsPandascoreMarketBlockUpdate
  | WsPandascoreMarketBlockDelete
  | HttpUpdatePandascoreMarket
  | WsSettlement
  | WsRealtimeBetlog
  | WsSetTradedWithGrid
  | WsUnsetTradedWithGrid
  | WsSetTradedWithScraper
  | WsUnsetTradedWithScraper;

const matchStatusWithMarkets: MatchStatus[] = [
  'set_up',
  'pre_match',
  'live',
  'finished',
  'settled',
  'canceled',
  'postponed'
];

interface WSMarketUpdateForMatch {
  type: 'market';
  action: 'update';
  model: CompetitorMarketState;
}

interface WSCsMatchScrapperDataUpdate {
  type: 'csgo_game_scoreboard';
  action: 'update';
  model: CsScrapperScoreboard;
}

interface WSDota2MatchSteamDataUpdate {
  type: 'steam_dota2_scoreboard';
  action: 'update';
  model: Dota2SteamScoreboard;
}

interface WSDota2MatchScrapperDataUpdate {
  type: 'dota2_game_scoreboard';
  action: 'update';
  model_id: number;
  model: Dota2ScrapperScoreboard;
}

interface WSMarketInitForMatch {
  type: 'init_ressource';
}

interface WSCompetitorsUpdate {
  type: 'competitor';
  action: 'update';
  model: Competitor;
}

interface WSCompetitorsUpdateForMatch {
  type: 'competitor_match';
  action: 'update';
  model: CompetitorMatchUpdate;
}

type HashiWsMessage =
  | WSMarketInitForMatch
  | WSMarketUpdateForMatch
  | WSCsMatchScrapperDataUpdate
  | WSDota2MatchScrapperDataUpdate
  | WSDota2MatchSteamDataUpdate
  | WSCompetitorsUpdate
  | WSCompetitorsUpdateForMatch;

function createCompetitorsWinnerMarketsRequestForMatch(
  matchId: string
): Observable<CompetitorMatch | null> {
  return request<CompetitorMatch | null>({
    url: getHashiUrl(`/matches/${matchId}`),
    headers: {
      ...getHashiHeaders()
    }
  }).pipe(
    map(({ data }) => data!),
    catchError(() => of(null))
  );
}

function createCompetitorsRequest(): Observable<CompetitorsRequest> {
  return request<CompetitorsRequest>({
    url: getHashiUrl('/competitors'),
    headers: getHashiHeaders()
  }).pipe(
    map(({ data }) => data!),
    catchError(() => of([]))
  );
}

function csLiveGameScrapperRequest(gameId: string): Observable<CsScrapperScoreboard[]> {
  return request<CsScrapperScoreboard>({
    url: getHashiUrl(`/scoreboards/maxbet/cs-go/games/${gameId}`),
    headers: getHashiHeaders()
  }).pipe(
    map(response => [response.data!]),
    catchError(() => of([]))
  );
}

function dota2LiveGameSteamRequest(gameId: string): Observable<Dota2SteamScoreboard[]> {
  return request<Dota2SteamScoreboard>({
    url: getHashiUrl(`/steam/dota-2/scoreboards/${gameId}`),
    headers: getHashiHeaders()
  }).pipe(
    map(response => [response.data!]),
    catchError(() => of([]))
  );
}

function dota2LiveGameScrapperRequest(gameId: string): Observable<Dota2ScrapperScoreboard[]> {
  return request<Dota2ScrapperScoreboard>({
    url: getHashiUrl(`/scoreboards/maxbet/dota-2/games/${gameId}`),
    headers: getHashiHeaders()
  }).pipe(
    map(response => {
      if (response.data === undefined) {
        throw new Error('No data received');
      }
      return [response.data];
    }),
    catchError(() =>
      request<Dota2ScrapperScoreboard>({
        url: getHashiUrl(`/scoreboards/ggbet/dota-2/games/${gameId}`),
        headers: getHashiHeaders()
      }).pipe(map(response => [response.data!]))
    ),
    catchError(() => of([]))
  );
}

function createCompetitorsMatchRequest(
  matchId: string | undefined,
  userType: UserType
): Observable<CompetitorOddsWithStatus[]> {
  // We return an empty array because we don't need to make call without matchId
  if (!(matchId && hasAccessToFeature('COMPETITORS', userType))) return of([]);

  const competitorsRequest = createCompetitorsRequest();
  const competitorsMatchMarketsRequest = createCompetitorsWinnerMarketsRequestForMatch(matchId);

  return zip(competitorsRequest, competitorsMatchMarketsRequest).pipe(
    map(([competitors, competitorsMatchMarkets]) => {
      if (competitorsMatchMarkets) {
        return competitors
          .map(competitor => {
            const competitorMatchData = competitorsMatchMarkets.competitors.find(
              searchedCompetitor => searchedCompetitor.competitor_slug === competitor.slug
            );
            return {
              ...competitor,
              ...(competitorMatchData || []),
              match_status: competitorMatchData?.status ?? null,
              scrapper_status: competitor.status,
              markets: competitorsMatchMarkets.markets.filter(
                market => market.competitor_slug === competitor.slug
              )
            };
          })
          .filter(data => data.markets.length > 0);
      }

      return [];
    }),
    catchError(() => of([]))
  );
}

function hashiFirstRequest(
  matchId: string | undefined,
  userType: UserType,
  gameId: string | false,
  videogame_slug: VideogameSlug | undefined
): Observable<HashiData> {
  if (!matchId || !videogame_slug) {
    return of({
      match_id: '',
      competitors: [],
      csScrapperData: [],
      dota2ScrapperData: [],
      dota2SteamData: []
    });
  }

  const competitorRequest = createCompetitorsMatchRequest(matchId, userType);

  const csScrapperRequest =
    gameId && videogame_slug === 'cs-go' && hasAccessToFeature('COMPETITORS', userType)
      ? csLiveGameScrapperRequest(gameId)
      : of([]);

  const dota2ScrapperRequest =
    gameId && videogame_slug === 'dota-2' && hasAccessToFeature('COMPETITORS', userType)
      ? dota2LiveGameScrapperRequest(gameId)
      : of([]);

  const dota2SteamRequest =
    gameId && videogame_slug === 'dota-2' && hasAccessToFeature('COMPETITORS', userType)
      ? dota2LiveGameSteamRequest(gameId)
      : of([]);

  return zip(competitorRequest, csScrapperRequest, dota2ScrapperRequest, dota2SteamRequest).pipe(
    map(([competitors, csScrapperData, dota2ScrapperData, dota2SteamData]) => ({
      match_id: matchId,
      competitors,
      csScrapperData,
      dota2ScrapperData,
      dota2SteamData
    }))
  );
}

function handleHashiMessage(hashiData: HashiData, message: HashiWsMessage): Observable<HashiData> {
  switch (message.type) {
    case 'init_ressource': {
      return of(hashiData);
    }

    case 'market': {
      switch (message.action) {
        case 'update': {
          const updatedCurrentDataCompetitors = hashiData.competitors.reduce<
            CompetitorOddsWithStatus[]
          >((acc, competitor) => {
            // If competitor already exist we update him
            if (competitor.slug === message.model.competitor_slug) {
              const updatedMarkets = competitor.markets.map(market => {
                if (
                  market.match_id === message.model.match_id &&
                  market.event_type === message.model.event_type &&
                  market.event_id === message.model.event_id &&
                  market.template === message.model.template &&
                  market.market_id === message.model.market_id
                ) {
                  return message.model;
                }

                return market;
              });

              const isMarketInUpdatedMarkets = updatedMarkets.find(
                market =>
                  market.match_id === message.model.match_id &&
                  market.event_type === message.model.event_type &&
                  market.event_id === message.model.event_id &&
                  market.template === message.model.template &&
                  market.market_id === message.model.market_id
              );

              // if market is new we add it into array
              if (!isMarketInUpdatedMarkets) {
                updatedMarkets.push(message.model);
              }

              return [
                ...acc,
                {
                  ...competitor,
                  markets: updatedMarkets
                }
              ];
            }

            return [...acc, competitor];
          }, []);

          return of({
            ...hashiData,
            competitors: updatedCurrentDataCompetitors
          });
        }
      }
    }

    case 'csgo_game_scoreboard': {
      const newScoreboard = message.model;

      if (hashiData.match_id !== newScoreboard.match_id.toString()) {
        return of(hashiData);
      }

      const gameIndex = hashiData.csScrapperData.findIndex(
        item => item.game_id === newScoreboard.game_id
      );

      if (gameIndex === -1) {
        return of({
          ...hashiData,
          csScrapperData: [...hashiData.csScrapperData, newScoreboard]
        });
      }

      const updatedGames = hashiData.csScrapperData;
      updatedGames[gameIndex] = newScoreboard;

      return of({
        ...hashiData,
        csScrapperData: updatedGames
      });
    }

    case 'steam_dota2_scoreboard': {
      if (hashiData.match_id !== message.model.match_id.toString()) {
        return of(hashiData);
      }

      const gameIndex = hashiData.dota2SteamData.findIndex(
        item => item.game_id === message.model.game_id
      );

      if (gameIndex === -1) {
        return of({
          ...hashiData,
          dota2SteamData: [...hashiData.dota2SteamData, message.model]
        });
      }

      const updatedGames = hashiData.dota2SteamData;
      updatedGames[gameIndex] = message.model;

      return of({
        ...hashiData,
        dota2SteamData: updatedGames
      });
    }

    case 'dota2_game_scoreboard': {
      if (hashiData.match_id !== message.model.match_id.toString()) {
        return of(hashiData);
      }

      const gameIndex = hashiData.dota2ScrapperData.findIndex(
        item => item.game_id === message.model_id
      );

      if (gameIndex === -1) {
        // if source is different from the other sources, we don't add the data
        const source = uniques(hashiData.dota2ScrapperData.map(item => item.source)).at(0);
        if (source && source !== message.model.source) {
          return of(hashiData);
        }
        return of({
          ...hashiData,
          dota2ScrapperData: [...hashiData.dota2ScrapperData, message.model]
        });
      }

      // if source is different from the current source, we don't update the data
      if (message.model.source !== hashiData.dota2ScrapperData[gameIndex].source) {
        return of(hashiData);
      }

      const updatedGames = hashiData.dota2ScrapperData;
      updatedGames[gameIndex] = message.model;

      return of({
        ...hashiData,
        dota2ScrapperData: updatedGames
      });
    }

    case 'competitor': {
      return of({
        ...hashiData,
        competitors: hashiData.competitors.map(competitor => {
          if (competitor.slug === message.model.slug) {
            return {
              ...competitor,
              scrapper_status: message.model.status
            };
          }
          return competitor;
        })
      });
    }

    case 'competitor_match': {
      return of({
        ...hashiData,
        competitors: hashiData.competitors.map(competitor => {
          if (
            competitor.slug === message.model.competitor_slug &&
            hashiData.match_id === message.model.match_id.toString()
          ) {
            return {
              ...competitor,
              match_status: message.model.status
            };
          }
          return competitor;
        })
      });
    }

    default: {
      return of(hashiData);
    }
  }
}

function createEmptyMatchMarkets(match: PandapiMatch): MatchMarkets {
  const games = (match.games as PandapiGameBase[]).map(game => ({
    id: game.id,
    position: game.position,
    markets: []
  }));

  return {
    id: match.id,
    markets: [],
    games
  };
}

function createMatchRequest(matchId: string): Observable<PandapiMatch> {
  return request<PandapiMatch>({
    url: getPandapiUrl(`/betting/matches/${matchId}`),
    headers: getPandapiHeaders()
  }).pipe(map(response => response.data!));
}

function createMarketRequest(matchId: string): Observable<MatchMarkets> {
  return request<MatchMarkets>({
    url: getApiUrl(`/matches/${matchId}/markets`),
    headers: getTangerineHeaders()
  }).pipe(map(response => response.data!));
}

function createIncidentsRequest(matchId: string): Observable<MatchIncident[]> {
  return request<MatchIncident[]>({
    url: getApiUrl(`/incidents/matches/${matchId}`),
    headers: getTangerineHeaders()
  }).pipe(
    map(response => response.data!),
    catchError(() => [])
  );
}

const createBlueprintRequest = (matchId: string) =>
  request<MatchBlueprint>({
    url: getApiUrl(`/matches/${matchId}/blueprint`),
    headers: getTangerineHeaders()
  }).pipe(
    map(response => response.data!),
    catchError(() => of(undefined))
  );

function getScheduleMetaData(matchMarkets: MatchMarkets): ScheduleMetaData {
  const isMarket = (market: Market): market is Market => !market.is_micromarket;
  const filteredGamesMarkets = matchMarkets.games.map(game => ({
    ...game,
    markets: game.markets.filter(isMarket)
  }));
  const filteredMatchMarkets = matchMarkets.markets.filter(isMarket);

  const totalGamesMarkets = filteredGamesMarkets.reduce(
    (acc, curr) => acc + curr.markets.filter(isMarket).length,
    0
  );
  const totalMatchMarkets = filteredMatchMarkets.filter(isMarket).length;
  const totalMarkets = totalGamesMarkets + totalMatchMarkets;
  const reviewedMatchMarkets = filteredMatchMarkets.reduce((acc, curr) => {
    if (curr.reviewed) {
      return acc + 1;
    }

    return acc;
  }, 0);

  const reviewedGamesMarkets = filteredGamesMarkets.reduce((acc, currentGame) => {
    const reviewedMarketForCurrentGame = currentGame.markets.reduce((acc, curr) => {
      if (curr.reviewed) {
        return acc + 1;
      }

      return acc;
    }, 0);
    return acc + reviewedMarketForCurrentGame;
  }, 0);

  const reviewedMarkets = reviewedMatchMarkets + reviewedGamesMarkets;

  return {
    markets: matchMarkets.markets,
    reviewed_counters: [
      {
        match_id: matchMarkets.id,
        reviewed: reviewedMarkets,
        total: totalMarkets
      }
    ]
  };
}

const createBlacklistedPlayersRequest = (matchId: string) =>
  request<BlacklistMatch[]>({
    url: getControleBackendUrl('/blacklist/matches'),
    headers: getPandapiHeaders(),
    query: { match_ids: matchId }
  }).pipe(
    map(response => response.data!),
    catchError(() => of([]))
  );

const createPandascoreMarketsRequest = (matchId: string) =>
  request<MatchMarkets>({
    url: getApiUrl(`/matches/${matchId}/pandascore_markets`),
    headers: getTangerineHeaders()
  }).pipe(map(response => response.data!));

const createSettlementRequest = (matchId: string) =>
  request<GameSettlement[]>({
    url: getApiUrl(`/settlements/matches/${matchId}`),
    headers: getTangerineHeaders()
  }).pipe(map(response => response.data));

const createRealtimeBetlogRequest = (matchId: string) =>
  request<RealtimeBetlogMarket[]>({
    url: getApiUrl(`/betlogs/matches/${matchId}/market_metrics`),
    headers: getTangerineHeaders()
  }).pipe(
    map(response => response.data!),
    catchError(() => of(undefined))
  );

function createMatchDataRequests(matchId: string, userType: UserType): Observable<MatchData> {
  return zip(createMatchRequest(matchId), createIncidentsRequest(matchId)).pipe(
    mergeMap(([match, incidents]) => {
      // we load markets only if match is at least in prematch status
      const marketRequest = matchStatusWithMarkets.includes(match.status)
        ? createMarketRequest(matchId)
        : of(undefined);

      const blueprintRequest =
        hasAccessToFeature('EDIT_MATCH_BLUEPRINT', userType) &&
        match.betting_metadata?.markets_created
          ? createBlueprintRequest(matchId)
          : of(undefined);

      const pandascoreMarketsRequest =
        hasAccessToFeature('BOOKMAKER_INPUT', userType) && match.betting_metadata?.inputs_enable
          ? createPandascoreMarketsRequest(matchId)
          : of(undefined);

      const blacklistedPlayersRequest = hasAccessToFeature('BLACKLIST', userType)
        ? createBlacklistedPlayersRequest(matchId)
        : of(undefined);

      const settlementsRequest = hasAccessToFeature('TRADER_INPUT', userType)
        ? createSettlementRequest(matchId)
        : of(undefined);

      const realtimeBetlogRequest = hasAccessToFeature('REALTIME_BETLOG', userType)
        ? createRealtimeBetlogRequest(matchId)
        : of(undefined);

      return zip(
        of(match),
        of(incidents),
        blueprintRequest,
        marketRequest,
        pandascoreMarketsRequest,
        blacklistedPlayersRequest,
        settlementsRequest,
        realtimeBetlogRequest
      );
    }),
    map(
      ([
        match,
        incidents,
        blueprint,
        markets,
        pandascoreMarkets,
        blacklistedPlayers,
        settlements,
        realtimeBetlog
      ]) => {
        const matchMarkets: MatchMarkets = markets ?? {
          id: match.id,
          markets: [],
          games: match.games.map(game => ({
            id: game.id,
            position: game.position,
            markets: []
          }))
        };

        const schedule_metadata = getScheduleMetaData(matchMarkets);

        return {
          match,
          markets: matchMarkets,
          schedule_metadata,
          incidents,
          blueprint,
          pandascoreMarkets: pandascoreMarkets ?? matchMarkets,
          blacklistedPlayers: blacklistedPlayers?.[0]?.suspicious_players,
          settlements,
          realtimeBetlog,
          updatesForBetbuilder: {
            markets: Date.now(),
            games: Date.now(),
            ...Object.fromEntries(match.games.map(game => [game.id, Date.now()]))
          }
        };
      }
    )
  );
}

function isLastVersion<T extends { updated_at: string }>(currentItem: T, item: T): boolean {
  return currentItem.updated_at <= item.updated_at;
}

function upsertInMarketList(markets: Market[], newMarket: Market): Market[] {
  const isMarketExists = markets.some(({ id }) => id === newMarket.id);

  if (isMarketExists) {
    return markets.map(market => {
      if (market.id === newMarket.id && isLastVersion(market, newMarket)) {
        return newMarket;
      }
      return market;
    });
  }

  return [...markets, newMarket];
}

function upsertMarket(markets: MatchMarkets, newMarket: Market): MatchMarkets {
  const { event_id, event_type } = newMarket;

  const isGameMarket = event_type === 'game';

  if (isGameMarket) {
    const games = markets.games.map(game => {
      if (game.id === event_id) {
        const markets = upsertInMarketList(game.markets || [], newMarket);
        return {
          ...game,
          markets
        };
      }

      return game;
    });

    return {
      ...markets,
      games
    };
  }

  // if is match market
  return {
    ...markets,
    markets: upsertInMarketList(markets.markets || [], newMarket)
  };
}

function deleteMarket(markets: MatchMarkets, marketId: string): MatchMarkets {
  const matchMarkets = removeItemWhen(markets.markets || [], market => market.id === marketId);
  const gameMarkets = markets.games.map(game => ({
    ...game,
    markets: removeItemWhen(game.markets || [], market => market.id === marketId)
  }));

  return {
    ...markets,
    markets: matchMarkets,
    games: gameMarkets
  };
}

function upsertMarkets(match: MatchMarkets, markets: Market[]): MatchMarkets {
  return markets.reduce((updatedMatch, market) => upsertMarket(updatedMatch, market), match);
}

function handleMatchMessage(currentData: MatchData, message: MatchMessage): Observable<MatchData> {
  switch (message.type) {
    case 'trader_inputs': {
      return of({
        ...currentData,
        markets: {
          ...currentData.markets,
          trader_inputs: message.model
        }
      });
    }

    case 'match': {
      return zip(createMatchRequest(`${message.model_id}`)).pipe(
        mergeMap(([match]) => {
          // we load markets only if match is at least in prematch status
          const marketRequest = matchStatusWithMarkets.includes(match.status)
            ? createMarketRequest(match.id.toString())
            : of(undefined);

          return zip(of(match), marketRequest);
        }),
        map(([match, markets]) => {
          const matchMarkets: MatchMarkets = markets ?? {
            id: match.id,
            markets: [],
            games: match.games.map(game => ({
              id: game.id,
              position: game.position,
              markets: []
            }))
          };
          const schedule_metadata = getScheduleMetaData(matchMarkets);

          // if match is live or finished we keep scoreboard from `live_scoreboard`
          if (['live', 'finished'].includes(match.status)) {
            switch (currentData.match.videogame.slug) {
              case 'league-of-legends': {
                const games: PandapiLolGame[] = (match.games as PandapiLolGame[]).map(game => {
                  const scoreGame = (currentData.match.games as PandapiLolGame[]).find(
                    ({ id }) => id === game.id
                  );
                  if (scoreGame) {
                    return {
                      ...game,
                      game_teams: scoreGame.game_teams,
                      live_timestamp: scoreGame.live_timestamp
                    };
                  }
                  return game;
                });

                return {
                  ...currentData,
                  match: { ...match, games },
                  schedule_metadata,
                  updatesForBetbuilder: getUpdatesForBetbuilder(currentData, match)
                };
              }
            }
          }

          return {
            ...currentData,
            match,
            schedule_metadata,
            updatesForBetbuilder: getUpdatesForBetbuilder(currentData, match)
          };
        })
      );
    }

    case 'betting_metadata': {
      switch (message.action) {
        case 'updated': {
          return zip(createMatchRequest(`${currentData.match.id}`)).pipe(
            mergeMap(([match]) => {
              // we load markets only if match is at least in prematch status
              const marketRequest = matchStatusWithMarkets.includes(match.status)
                ? createMarketRequest(match.id.toString())
                : of(undefined);

              return zip(of(match), marketRequest);
            }),
            map(([match, markets]) => {
              const matchUpdated: PandapiMatch = {
                ...currentData.match,
                betting_metadata: match.betting_metadata,
                status: match.status
              };

              const matchMarkets: MatchMarkets = markets ?? {
                id: match.id,
                markets: [],
                games: match.games.map(game => ({
                  id: game.id,
                  position: game.position,
                  markets: []
                }))
              };
              const schedule_metadata = getScheduleMetaData(matchMarkets);

              return {
                ...currentData,
                match: matchUpdated,
                schedule_metadata
              };
            })
          );
        }
      }
    }

    case 'incident': {
      switch (message.action) {
        case 'updated': {
          const incidentIndex = currentData.incidents.findIndex(
            incident => incident.id === message.model.id
          );
          const incidents = [...currentData.incidents];

          if (incidentIndex >= 0) {
            incidents.splice(incidentIndex, 1, message.model);
          } else {
            incidents.push(message.model);
          }

          return of({
            ...currentData,
            incidents
          });
        }

        case 'deleted': {
          const incidentIndex = currentData.incidents.findIndex(
            incident => incident.id === message.model.id
          );
          const incidents = [...currentData.incidents];

          if (incidentIndex >= 0) {
            incidents.splice(incidentIndex, 1);
          }

          return of({
            ...currentData,
            incidents
          });
        }
      }
    }

    case 'market': {
      switch (message.action) {
        case 'inserted':
        case 'updated': {
          if (message.model) {
            const markets = upsertMarket(currentData.markets, message.model);
            const schedule_metadata = getScheduleMetaData(markets);
            return of({
              ...currentData,
              markets,
              schedule_metadata,
              updatesForBetbuilder: {
                ...currentData.updatesForBetbuilder,
                markets: Date.now()
              }
            });
          }
          break;
        }
        case 'deleted': {
          const markets = deleteMarket(currentData.markets, message.model_id);
          const schedule_metadata = getScheduleMetaData(markets);
          return of({
            ...currentData,
            markets,
            schedule_metadata,
            updatesForBetbuilder: {
              ...currentData.updatesForBetbuilder,
              markets: Date.now()
            }
          });
        }
        case 'http_update': {
          const markets = upsertMarkets(currentData.markets, message.model);
          const schedule_metadata = getScheduleMetaData(markets);
          return of({
            ...currentData,
            markets,
            schedule_metadata,
            updatesForBetbuilder: {
              ...currentData.updatesForBetbuilder,
              markets: Date.now()
            }
          });
        }

        default:
          break;
      }
      break;
    }

    case 'refresh_markets': {
      return zip(createMarketRequest(currentData.match.id.toString())).pipe(
        mergeMap(([markets]) => {
          const schedule_metadata = getScheduleMetaData(markets);

          return of({
            ...currentData,
            markets,
            schedule_metadata,
            updatesForBetbuilder: {
              ...currentData.updatesForBetbuilder,
              markets: Date.now()
            }
          });
        })
      );
    }

    case 'clear_markets': {
      // remove all markets because unbook doesn't send WS event to remove markets
      return of({
        ...currentData,
        markets: createEmptyMatchMarkets(currentData.match),
        schedule_metadata: {
          markets: [],
          reviewed_counters: []
        },
        updatesForBetbuilder: {
          ...currentData.updatesForBetbuilder,
          markets: Date.now()
        }
      });
    }

    case 'match_blueprint': {
      return of({
        ...currentData,
        blueprint: message.model
      });
    }

    case 'pandascore_market': {
      switch (message.action) {
        case 'inserted':
        case 'updated': {
          if (message.model) {
            return of({
              ...currentData,
              pandascoreMarkets: upsertMarket(currentData.pandascoreMarkets, message.model)
            });
          }
          break;
        }
        case 'deleted': {
          return of({
            ...currentData,
            pandascoreMarkets: deleteMarket(currentData.pandascoreMarkets, message.model_id)
          });
        }
        case 'http_update': {
          const matchMarkets = upsertMarkets(currentData.pandascoreMarkets, message.model);
          return of({
            ...currentData,
            pandascoreMarkets: matchMarkets
          });
        }

        default:
          break;
      }
      break;
    }

    case 'settlements': {
      switch (message.action) {
        case 'updated': {
          if (message.model) {
            return of({
              ...currentData,
              settlements: message.model
            });
          }
          break;
        }

        default:
          break;
      }
      break;
    }
    case 'betlog_market_metrics': {
      switch (message.action) {
        case 'updated': {
          if (message.model) {
            return of({
              ...currentData,
              realtimeBetlog: message.model
            });
          }
          break;
        }
        default:
          break;
      }
      break;
    }

    case 'set_traded_with_grid': {
      if (currentData.match.betting_metadata) {
        return of({
          ...currentData,
          match: {
            ...currentData.match,
            betting_metadata: {
              ...currentData.match.betting_metadata,
              traded_with_grid: true
            }
          }
        });
      }

      return of(currentData);
    }

    case 'unset_traded_with_grid': {
      if (currentData.match.betting_metadata) {
        return of({
          ...currentData,
          match: {
            ...currentData.match,
            betting_metadata: {
              ...currentData.match.betting_metadata,
              traded_with_grid: false
            }
          }
        });
      }

      return of(currentData);
    }

    case 'set_traded_with_scraper': {
      if (currentData.match.betting_metadata) {
        return of({
          ...currentData,
          match: {
            ...currentData.match,
            betting_metadata: {
              ...currentData.match.betting_metadata,
              traded_with_scraper: true
            }
          }
        });
      }

      return of(currentData);
    }

    case 'unset_traded_with_scraper': {
      if (currentData.match.betting_metadata) {
        return of({
          ...currentData,
          match: {
            ...currentData.match,
            betting_metadata: {
              ...currentData.match.betting_metadata,
              traded_with_scraper: false
            }
          }
        });
      }

      return of(currentData);
    }
  }

  return of(currentData);
}

export {
  createCompetitorsRequest,
  createMatchDataRequests,
  handleMatchMessage,
  createCompetitorsMatchRequest,
  handleHashiMessage,
  hashiFirstRequest
};
export type { MatchMessage };
